import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { fetchPeriodReportAction, setError } from "../slices/periodReportSlice";
import {
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
  format,
} from "date-fns";

export type PeriodPreset =
  | "last_30_days"
  | "current_month"
  | "last_month"
  | "current_year"
  | "last_year"
  | "custom";

export const usePeriodReportBuilder = () => {
  const dispatch = useDispatch();

  const report = useSelector((state: RootState) => state.periodReport.report);
  const loading = useSelector((state: RootState) => state.periodReport.loading);
  const error = useSelector((state: RootState) => state.periodReport.error);

  const [selectedPeriod, setSelectedPeriod] =
    useState<PeriodPreset>("current_month");
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

  const handlePeriodChange = useCallback(
    (period: PeriodPreset) => {
      setSelectedPeriod(period);
      dispatch(setError(null));
    },
    [dispatch],
  );

  const getDateRange = useCallback(
    (preset: PeriodPreset): { start: Date; end: Date } | null => {
      const today = new Date();
      switch (preset) {
        case "last_30_days":
          return { start: subDays(today, 30), end: today };
        case "current_month":
          return { start: startOfMonth(today), end: endOfMonth(today) };
        case "last_month": {
          const lastMonth = subMonths(today, 1);
          return {
            start: startOfMonth(lastMonth),
            end: endOfMonth(lastMonth),
          };
        }
        case "current_year":
          return { start: startOfYear(today), end: endOfYear(today) };
        case "last_year": {
          const lastYear = subYears(today, 1);
          return {
            start: startOfYear(lastYear),
            end: endOfYear(lastYear),
          };
        }
        case "custom":
          if (customStartDate && customEndDate) {
            return { start: customStartDate, end: customEndDate };
          }
          return null;
        default:
          return null;
      }
    },
    [customStartDate, customEndDate],
  );

  const handleGenerateReport = useCallback(() => {
    const dateRange = getDateRange(selectedPeriod);
    if (!dateRange) {
      return;
    }

    const periodStart = format(dateRange.start, "yyyy-MM-dd");
    const periodEnd = format(dateRange.end, "yyyy-MM-dd");

    dispatch(
      fetchPeriodReportAction({
        reportType: "profit_and_loss",
        periodStart,
        periodEnd,
      }),
    );
  }, [dispatch, selectedPeriod, getDateRange]);

  // Validate that the selected period can generate a valid date range
  const canGenerate = getDateRange(selectedPeriod) !== null;

  return {
    selectedPeriod,
    setSelectedPeriod: handlePeriodChange,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    report,
    loading,
    error,
    handleGenerateReport,
    canGenerate,
  };
};
